import { z } from "zod"

/**
 * Runtime env validation.
 * Boot sırasında main.tsx'te validate edilir; başarısızsa app açılmaz.
 */

const envSchema = z.object({
  VITE_API_BASE_URL: z.string().url(),
  // CNI panel ayrı bir backend'de duracak (CNI-Backend SuperAdmin namespace).
  // Local stg'de Vite proxy üzerinden `/cni-api` (relative) kullanılır;
  // Jenkins build'de absolute URL (`https://cni-api-dev.n2nenergy.com/api`).
  // ADR #037: hedef branch'ten bağımsız olarak CNI **dev** ortamıdır.
  // Empty string normalize: undefined'a çevir → cni-client Hems base'ine düşer.
  VITE_CNI_API_BASE_URL: z
    .string()
    .optional()
    .transform((v) => (v && v.trim().length > 0 ? v : undefined))
    .refine(
      (v) => v === undefined || /^https?:\/\//.test(v) || v.startsWith("/"),
      "Must be empty, an absolute http(s) URL, or a relative path starting with `/`",
    ),
  // CNI SSH Tunnel API (cni-ssh-devices, ADR #023) — bağımsız Python FastAPI backend.
  // Boşsa cni-ssh-client localhost:8000'e fallback eder (lokal dev). Empty → undefined
  // (boş string url() validasyonunu patlatmasın diye stg pattern'iyle aynı).
  VITE_CNI_SSH_API_BASE_URL: z
    .string()
    .optional()
    .transform((v) => (v && v.trim().length > 0 ? v : undefined))
    .refine(
      (v) => v === undefined || /^https?:\/\//.test(v) || v.startsWith("/"),
      "Must be empty, an absolute http(s) URL, or a relative path starting with `/`",
    ),
  // HEMS SSH Tunnel API (hems-ssh-devices) — bağımsız Python FastAPI backend.
  // EC100 reverse-SSH tunnel envanteri + uzaktan Node-RED yazılım güncelleme.
  // Boşsa hems-ssh-client localhost:8000'e fallback eder (lokal dev). Empty → undefined.
  VITE_HEMS_SSH_API_BASE_URL: z
    .string()
    .optional()
    .transform((v) => (v && v.trim().length > 0 ? v : undefined))
    .refine(
      (v) => v === undefined || /^https?:\/\//.test(v) || v.startsWith("/"),
      "Must be empty, an absolute http(s) URL, or a relative path starting with `/`",
    ),
  VITE_SIGNALR_URL: z.string().url(),
  // Küre derin katmanının raster zemini (ADR #041). Slippy-map şablonu:
  // `{z}/{x}/{y}` zorunlu, `{s}` (alt alan adı) ve `{r}` (retina `@2x`) isteğe
  // bağlı. Boş bırakılırsa CNI'daki OpenStreetMap sokak karoları kullanılır.
  // Kendi tile proxy'mize geçiş bu tek değişkenle yapılır.
  VITE_MAP_TILE_URL: z
    .string()
    .optional()
    .transform((v) => (v && v.trim().length > 0 ? v : undefined))
    .refine(
      (v) => v === undefined || /^https?:\/\//.test(v),
      "Must be empty or an absolute http(s) tile template",
    ),
  // CARTO key is public in the browser bundle; restrict it to allowed referrers.
  VITE_MAP_TILE_API_KEY: z
    .string()
    .optional()
    .transform((v) => (v && v.trim().length > 0 ? v.trim() : undefined)),
  // Sağlayıcı ToS'u gereği ekranda gösterilen atıf. Boşsa varsayılan kaynağın
  // atfı kullanılır — kaynağı değiştirip atfı unutmak ihlal olurdu.
  VITE_MAP_TILE_ATTRIBUTION: z
    .string()
    .optional()
    .transform((v) => (v && v.trim().length > 0 ? v : undefined)),
  VITE_DEFAULT_LOCALE: z.enum(["tr", "en"]).default("tr"),
  VITE_MOCK_API: z
    .enum(["true", "false"])
    .default("true")
    .transform((v) => v === "true"),
})

export type Env = z.infer<typeof envSchema>

let cached: Env | null = null

export function getEnv(): Env {
  if (cached) {
    return cached
  }
  const result = envSchema.safeParse(import.meta.env)
  if (!result.success) {
    console.error("Invalid environment variables:", result.error.flatten().fieldErrors)
    throw new Error("Environment validation failed. Check .env.example.")
  }
  cached = result.data
  return cached
}
